import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight, FaSearch } from 'react-icons/fa';

const isEmpty = (v) => v === null || v === undefined || v === '';

const compareValues = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'it', { numeric: true, sensitivity: 'base' });
};

const ALIGN_JUSTIFY = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };
const ALIGN_TEXT = { left: 'text-left', center: 'text-center', right: 'text-right' };

const isSortable = (col) => (col.sortable !== undefined ? col.sortable : !!col.sortValue);

/**
 * Tabella React con ricerca, ordinamento e paginazione.
 *
 * Modalita client (default): passa `rows`; ordina/filtra/pagina in memoria.
 * Modalita server: passa `server={{ url, extraData?, reloadKey? }}`; la tabella invia
 * { start, length, search: { value }, order: [{ column, dir }], ...extraData() } in POST
 * e si aspetta { data, recordsTotal, recordsFiltered }. `column` e l'indice della colonna
 * in `columns` (oppure `col.serverIndex`).
 *
 * Colonne: { key, header, render(row), sortValue?(row), sortable?, align?, headerClassName?, cellClassName?, serverIndex? }
 */
const DataGrid = ({
    columns,
    rows = [],
    rowKey,
    server = null,
    searchKeys = [],
    searchPlaceholder = 'Cerca...',
    initialSort = null,
    pageSizeOptions = [10, 25, 50, 100],
    defaultPageSize = 25,
    emptyMessage = 'La ricerca non ha portato alcun risultato.'
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sort, setSort] = useState(initialSort);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    const [serverRows, setServerRows] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);
    const [serverFiltered, setServerFiltered] = useState(0);
    const [loading, setLoading] = useState(!!server);
    const [loadError, setLoadError] = useState(null);

    const serverRef = useRef(server);
    serverRef.current = server;
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const serverUrl = server ? server.url : null;
    const serverReloadKey = server ? server.reloadKey : undefined;

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        if (!serverUrl) return undefined;
        const controller = new AbortController();
        const cols = columnsRef.current;
        const sortCol = sort ? cols.find((c) => c.key === sort.key) : null;
        const sortIndex = sortCol ? (sortCol.serverIndex !== undefined ? sortCol.serverIndex : cols.indexOf(sortCol)) : null;
        const extra = serverRef.current && serverRef.current.extraData ? serverRef.current.extraData() : {};
        const body = {
            draw: 1,
            start: (page - 1) * pageSize,
            length: pageSize,
            search: { value: debouncedSearch },
            order: sortIndex !== null ? [{ column: sortIndex, dir: sort.dir }] : [],
            ...extra
        };

        setLoading(true);
        setLoadError(null);
        fetch(serverUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: controller.signal
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.error) throw new Error(json.error);
                const filtered = Number(json.recordsFiltered) || 0;
                setServerRows(json.data || []);
                setServerTotal(Number(json.recordsTotal) || 0);
                setServerFiltered(filtered);
                setLoading(false);
                const lastPage = Math.max(1, Math.ceil(filtered / pageSize));
                if (page > lastPage) setPage(lastPage);
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                console.error('DataGrid: errore caricamento dati', err);
                setLoadError(err.message || 'Errore nel caricamento dei dati');
                setLoading(false);
            });

        return () => controller.abort();
    }, [serverUrl, serverReloadKey, page, pageSize, debouncedSearch, sort]);

    const searched = useMemo(() => {
        if (server) return rows;
        const term = search.trim().toLowerCase();
        if (!term || searchKeys.length === 0) return rows;
        return rows.filter((row) =>
            searchKeys.some((key) => {
                const value = typeof key === 'function' ? key(row) : row[key];
                return !isEmpty(value) && String(value).toLowerCase().includes(term);
            })
        );
    }, [server, rows, search, searchKeys]);

    const sorted = useMemo(() => {
        if (server) return searched;
        const col = sort && columns.find((c) => c.key === sort.key);
        if (!col || !col.sortValue) return searched;
        const direction = sort.dir === 'asc' ? 1 : -1;
        return [...searched].sort((ra, rb) => {
            const a = col.sortValue(ra);
            const b = col.sortValue(rb);
            const aEmpty = isEmpty(a);
            const bEmpty = isEmpty(b);
            if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
            return compareValues(a, b) * direction;
        });
    }, [server, searched, sort, columns]);

    const total = server ? serverFiltered : sorted.length;
    const grandTotal = server ? serverTotal : rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const visibleRows = server ? serverRows : sorted.slice(startIndex, startIndex + pageSize);

    const windowStart = Math.max(1, Math.min(safePage - 2, totalPages - 4));
    const windowEnd = Math.min(totalPages, windowStart + 4);
    const pageNumbers = [];
    for (let p = windowStart; p <= windowEnd; p++) pageNumbers.push(p);

    const handleSort = (col) => {
        if (!isSortable(col)) return;
        setSort((prev) => (prev && prev.key === col.key ? { key: col.key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: col.key, dir: 'asc' }));
        setPage(1);
    };

    const pageButtonClass = (active) =>
        `min-w-[2rem] h-8 px-2 rounded-lg text-xs font-semibold border transition-colors ${active
            ? 'bg-red-500 text-white border-red-500'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`;

    const showInitialLoading = !!server && loading && serverRows.length === 0;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
                <div className="relative w-full sm:w-72">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        placeholder={searchPlaceholder}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition"
                    />
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500">
                    Visualizza
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:ring-2 focus:ring-red-400 focus:border-red-400"
                    >
                        {pageSizeOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                    elementi
                </label>
            </div>

            <div className={`overflow-x-auto transition-opacity ${server && loading && !showInitialLoading ? 'opacity-60' : ''}`} aria-busy={!!server && loading}>
                <table className="w-full">
                    <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-100">
                            {columns.map((col) => {
                                const align = col.align || 'left';
                                const sortable = isSortable(col);
                                const active = sort && sort.key === col.key;
                                const SortIcon = !sortable ? null : active ? (sort.dir === 'asc' ? FaSortUp : FaSortDown) : FaSort;
                                return (
                                    <th
                                        key={col.key}
                                        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                                        className={`${col.headerClassName || 'px-4'} py-3.5 text-[10px] font-bold tracking-widest uppercase text-gray-400 ${ALIGN_TEXT[align]}`}
                                    >
                                        {sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => handleSort(col)}
                                                className={`inline-flex items-center gap-1.5 w-full uppercase tracking-widest font-bold hover:text-gray-600 transition-colors ${ALIGN_JUSTIFY[align]} ${active ? 'text-gray-700' : ''}`}
                                            >
                                                {col.header}
                                                {SortIcon && <SortIcon className={`text-[10px] ${active ? 'text-red-500' : 'text-gray-300'}`} />}
                                            </button>
                                        ) : col.header}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {showInitialLoading ? (
                            <tr>
                                <td colSpan={columns.length} className="py-12 text-center">
                                    <div className="inline-block w-7 h-7 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                                </td>
                            </tr>
                        ) : loadError ? (
                            <tr>
                                <td colSpan={columns.length} className="py-12 text-center text-sm text-red-500">{loadError}</td>
                            </tr>
                        ) : visibleRows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-12 text-center text-sm text-gray-400">{emptyMessage}</td>
                            </tr>
                        ) : (
                            visibleRows.map((row) => (
                                <tr key={typeof rowKey === 'function' ? rowKey(row) : row[rowKey]} className="hover:bg-gray-50/70 transition-colors">
                                    {columns.map((col) => (
                                        <td key={col.key} className={`${col.cellClassName || 'px-4'} py-3.5 ${ALIGN_TEXT[col.align || 'left']}`}>
                                            {col.render(row)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
                <span>
                    {total === 0
                        ? 'Vista da 0 a 0 di 0 elementi'
                        : `Vista da ${startIndex + 1} a ${Math.min(startIndex + pageSize, total)} di ${total} elementi`}
                    {(server ? debouncedSearch.trim() : search.trim()) && total !== grandTotal ? ` (filtrati da ${grandTotal} elementi totali)` : ''}
                </span>
                {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => setPage(Math.max(1, safePage - 1))}
                            disabled={safePage === 1}
                            className={`${pageButtonClass(false)} disabled:opacity-40 disabled:cursor-not-allowed`}
                            aria-label="Pagina precedente"
                        >
                            <FaChevronLeft className="mx-auto text-[10px]" />
                        </button>
                        {pageNumbers.map((p) => (
                            <button key={p} type="button" onClick={() => setPage(p)} className={pageButtonClass(p === safePage)}>
                                {p}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                            disabled={safePage === totalPages}
                            className={`${pageButtonClass(false)} disabled:opacity-40 disabled:cursor-not-allowed`}
                            aria-label="Pagina successiva"
                        >
                            <FaChevronRight className="mx-auto text-[10px]" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataGrid;
